import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/movie' },
  { label: '电视剧', to: '/tv' },
  { label: '资讯', to: '/news' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-colors duration-300 ${
        transparent
          ? 'bg-gradient-to-b from-foreground-950/70 to-transparent'
          : 'bg-background-50/95 backdrop-blur border-b border-background-200/70'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-[72px] items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 rounded-lg bg-accent-500 flex items-center justify-center text-foreground-950 text-xl">
              <i className="ri-film-fill" />
            </span>
            <span
              className={`font-heading text-xl font-extrabold tracking-tight ${
                transparent ? 'text-background-50' : 'text-foreground-950'
              }`}
            >
              神马影视
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-accent-500'
                      : transparent
                        ? 'text-background-100 hover:text-accent-400'
                        : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center">
            <form
              onSubmit={submitSearch}
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors ${
                transparent
                  ? 'border-background-50/40 bg-foreground-950/20'
                  : 'border-background-200 bg-background-100'
              }`}
            >
              <span
                className={`w-4 h-4 flex items-center justify-center ${
                  transparent ? 'text-background-100' : 'text-foreground-500'
                }`}
              >
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、资讯"
                className={`w-40 bg-transparent text-sm outline-none ${
                  transparent
                    ? 'text-background-50 placeholder:text-background-200/80'
                    : 'text-foreground-950 placeholder:text-foreground-500'
                }`}
              />
            </form>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="打开导航菜单"
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md text-2xl ${
              transparent ? 'text-background-50' : 'text-foreground-950'
            }`}
          >
            <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 animate-fade-in">
          <div className="px-4 py-4 space-y-3">
            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2"
            >
              <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、资讯"
                className="w-full bg-transparent text-sm outline-none text-foreground-950"
              />
            </form>
            <nav className="flex flex-col">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `px-3 py-2.5 rounded-md text-sm font-medium ${
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}